import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { applyTheme } from '../utils/themes';
import api from '../api';

export default function ApplyPage() {
  const { guildId } = useParams();
  const [questions, setQuestions] = useState([]);
  const [guild, setGuild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const [discordId, setDiscordId] = useState('');
  const [username, setUsername] = useState('');
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    applyTheme('brazyy_pink');
    Promise.all([
      api.get(`/api/applications/${guildId}/questions`),
      api.get(`/api/guilds/${guildId}`).catch(() => null),
    ]).then(([qRes, gRes]) => {
      setQuestions(qRes.data);
      if (gRes) setGuild(gRes.data);
    }).catch(() => setError('This server does not have applications enabled.'))
      .finally(() => setLoading(false));
  }, [guildId]);

  const handleSubmit = async () => {
    setError(null);
    if (!discordId.trim()) return setError('Please enter your Discord ID.');
    if (!username.trim()) return setError('Please enter your Discord username.');

    for (const q of questions) {
      if (q.required && !answers[q.id]?.trim()) {
        return setError(`"${q.question_text}" is required.`);
      }
      if (q.min_length && answers[q.id] && answers[q.id].length < q.min_length) {
        return setError(`"${q.question_text}" must be at least ${q.min_length} characters.`);
      }
      if (q.max_length && answers[q.id] && answers[q.id].length > q.max_length) {
        return setError(`"${q.question_text}" must be no more than ${q.max_length} characters.`);
      }
    }

    setSubmitting(true);
    try {
      await api.post(`/api/applications/${guildId}/submit`, {
        applicant_discord_id: discordId.trim(),
        applicant_username: username.trim(),
        applicant_display_name: username.trim(),
        answers: questions.map(q => ({
          question_id: q.id,
          answer_text: answers[q.id] || '',
        })),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', boxSizing: 'border-box',
    border: '2px solid var(--border)', borderRadius: '10px',
    fontSize: '14px', fontFamily: 'var(--font-body)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
    transition: 'border-color 0.2s',
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gradient-hero)', fontFamily: 'var(--font-body)' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
        <div style={{ color: 'var(--text-secondary)' }}>Loading application form...</div>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gradient-hero)', fontFamily: 'var(--font-body)' }}>
      <div style={{ textAlign: 'center', maxWidth: '480px', padding: '40px' }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '28px', color: 'var(--text-primary)', marginBottom: '12px' }}>Application Submitted!</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '16px', lineHeight: 1.6 }}>
          Your application has been submitted to <strong>{guild?.name || 'the server'}</strong>. An admin will review it and get back to you.
        </p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gradient-hero)', fontFamily: 'var(--font-body)', padding: '40px 20px' }}>
      <div style={{ maxWidth: '640px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '100px', padding: '6px 16px', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px', boxShadow: 'var(--shadow-card)' }}>
            <span style={{ color: 'var(--accent-primary)' }}>✦</span> Staff Application <span style={{ color: 'var(--accent-primary)' }}>✦</span>
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '36px', fontWeight: 900, color: 'var(--text-primary)', marginBottom: '8px' }}>
            {guild?.name || 'Staff Application'}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
            Fill out all required fields and submit your application below.
          </p>
        </div>

        {/* Form card */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '20px', padding: '32px', boxShadow: 'var(--shadow)' }}>

          {/* Identity section */}
          <div style={{ marginBottom: '28px', paddingBottom: '28px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: '16px' }}>Your Discord Info</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Discord Username <span style={{ color: 'var(--danger)' }}>*</span></label>
                <input type="text" value={username} onChange={e => setUsername(e.target.value)}
                  placeholder="e.g. brazyy" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
              <div>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Discord ID <span style={{ color: 'var(--danger)' }}>*</span></label>
                <input type="text" value={discordId} onChange={e => setDiscordId(e.target.value)}
                  placeholder="e.g. 123456789012345678" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  In Discord: Settings → Advanced → Enable Developer Mode, then right-click your name → Copy User ID
                </div>
              </div>
            </div>
          </div>

          {/* Questions */}
          {questions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '14px' }}>
              No questions have been added to this application yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '28px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)' }}>Application Questions</div>
              {questions.map((q, i) => (
                <div key={q.id}>
                  <label style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: '8px' }}>
                    {i + 1}. {q.question_text}
                    {q.required && <span style={{ color: 'var(--danger)', marginLeft: '4px' }}>*</span>}
                    {!q.required && <span style={{ color: 'var(--text-muted)', fontSize: '12px', marginLeft: '6px' }}>(optional)</span>}
                  </label>

                  {q.question_type === 'multiple_choice' && q.options ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {(Array.isArray(q.options) ? q.options : JSON.parse(q.options)).map((opt, oi) => (
                        <label key={oi} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: answers[q.id] === opt ? 'var(--accent-soft)' : 'var(--bg-secondary)', border: `2px solid ${answers[q.id] === opt ? 'var(--accent-primary)' : 'var(--border)'}`, borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s' }}>
                          <input type="radio" name={`q_${q.id}`} value={opt}
                            checked={answers[q.id] === opt}
                            onChange={() => setAnswers(p => ({ ...p, [q.id]: opt }))}
                            style={{ accentColor: 'var(--accent-primary)' }} />
                          <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: answers[q.id] === opt ? 600 : 400 }}>{opt}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div>
                      <textarea
                        value={answers[q.id] || ''}
                        onChange={e => setAnswers(p => ({ ...p, [q.id]: e.target.value }))}
                        placeholder="Type your answer here..."
                        rows={4}
                        style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                        onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                        onBlur={e => e.target.style.borderColor = 'var(--border)'}
                      />
                      {(q.min_length || q.max_length) && (
                        <div style={{ fontSize: '11px', color: answers[q.id]?.length > (q.max_length || Infinity) ? 'var(--danger)' : 'var(--text-muted)', marginTop: '4px', textAlign: 'right' }}>
                          {answers[q.id]?.length || 0}{q.max_length ? `/${q.max_length}` : ''} characters
                          {q.min_length && (answers[q.id]?.length || 0) < q.min_length ? ` (minimum ${q.min_length})` : ''}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {error && (
            <div style={{ background: '#FFF0F0', border: '1px solid #FFB0B0', borderRadius: '10px', padding: '12px 16px', color: '#CC0000', fontSize: '14px', marginBottom: '20px' }}>
              {error}
            </div>
          )}

          <button onClick={handleSubmit} disabled={submitting}
            style={{
              width: '100%', padding: '16px', background: submitting ? 'var(--text-muted)' : 'var(--gradient-accent)',
              color: '#fff', border: 'none', borderRadius: '12px',
              fontSize: '16px', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-display)', boxShadow: submitting ? 'none' : 'var(--shadow)',
            }}>
            {submitting ? 'Submitting...' : '📤 Submit Application'}
          </button>
        </div>

        <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '12px', color: 'var(--text-muted)' }}>
          Powered by Brazyy's Clock-In
        </p>
      </div>
    </div>
  );
}
